import type { FeedbackAttachment } from "@stockline/shared/types/core/feedback";

/**
 * Convert any File into a FeedbackAttachment (base64 data URL + name + mime +
 * raw byte size). Images are downscaled to MAX_DIM/JPEG-encoded so a 4 MB
 * phone photo lands at ~400 KB; everything else passes through as-is.
 *
 * Hard cap: MAX_RAW_BYTES per file (post-downscale for images). Larger files
 * are rejected so we don't blow up the `attachments: jsonb` column on push.
 */

const MAX_DIM = 1920;
const JPEG_QUALITY = 0.85;
const MAX_RAW_BYTES = 8 * 1024 * 1024; // 8 MB raw per file

export class FileTooLargeError extends Error {
  constructor(
    public file: File,
    public maxBytes: number,
  ) {
    super(`File too large: ${file.name}`);
    this.name = "FileTooLargeError";
  }
}

export async function ingestFile(file: File): Promise<FeedbackAttachment> {
  const isImage = file.type.startsWith("image/");
  if (isImage) {
    const dataUrl = await downscaleImageFile(file);
    const bytes = estimateDataUrlBytes(dataUrl);
    // Even after the downscale-to-1920px pass, a wide-aspect screenshot
    // can land above the cap. Reject so the server-side Zod max never has
    // to. Source-file size isn't a useful proxy here — encoded JPEG can
    // shrink or grow vs the original.
    if (bytes > MAX_RAW_BYTES) {
      throw new FileTooLargeError(file, MAX_RAW_BYTES);
    }
    return {
      dataUrl,
      name: file.name || "image",
      mime: "image/jpeg",
      size: bytes,
    };
  }
  if (file.size > MAX_RAW_BYTES) {
    throw new FileTooLargeError(file, MAX_RAW_BYTES);
  }
  const dataUrl = await readAsDataUrl(file);
  return {
    dataUrl,
    name: file.name || "file",
    mime: file.type || "application/octet-stream",
    size: file.size,
  };
}

async function downscaleImageFile(file: File): Promise<string> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const { naturalWidth: w, naturalHeight: h } = img;
    const longest = Math.max(w, h);
    const scale = longest > MAX_DIM ? MAX_DIM / longest : 1;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to decode image"));
    img.src = src;
  });
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result as string);
    fr.onerror = () => reject(fr.error ?? new Error("Failed to read file"));
    fr.readAsDataURL(file);
  });
}

function estimateDataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(",");
  const base64 = comma === -1 ? dataUrl : dataUrl.slice(comma + 1);
  // Base64 inflates 4 chars per 3 bytes, minus padding "=" chars.
  return Math.floor((base64.length * 3) / 4);
}

/** Pull files of any kind off a drop / clipboard transfer object. */
export function extractFiles(items: DataTransferItemList | null): File[] {
  if (!items) return [];
  const out: File[] = [];
  for (const item of Array.from(items)) {
    if (item.kind !== "file") continue;
    const f = item.getAsFile();
    if (f) out.push(f);
  }
  return out;
}

/**
 * Best-effort byte → "1.2 MB" formatter. Image attachments report their
 * post-downscale data-URL byte estimate so the displayed size reflects what
 * the network will actually carry.
 */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
